import firebase from "firebase/compat/app";
import { updateProfile, verifyBeforeUpdateEmail } from "firebase/auth";

// Add the Firebase products that you want to use
import "firebase/compat/auth";
import "firebase/compat/firestore";

class FirebaseAuthBackend {
  constructor(firebaseConfig) {
    if (firebaseConfig) {
      // Initialize Firebase
      firebase.initializeApp(firebaseConfig);
      firebase.auth().onAuthStateChanged((user) => {
        if (user) {
          localStorage.setItem("authUser", JSON.stringify(user));
        } else {
          localStorage.removeItem("authUser");
        }
      });

      this.auth = firebase.auth();
      // this.db = firebase.database()
      this.firestore = firebase.firestore();
    }
  }

  /**
   * Registers the user with given details
   */
  registerUser = (email, password, firstName, lastName) => {
    return new Promise((resolve, reject) => {
      firebase
        .auth()
        .createUserWithEmailAndPassword(email, password)
        .then(
          async (user) => {
            // Set user display name
            await updateProfile(user.user, {
              displayName: `${firstName} ${lastName}`,
            });

            resolve({ message: "success", user: firebase.auth().currentUser });
          },
          (error) => {
            console.error(error);
            reject(this._handleError(error));
          }
        );
    });
  };

  /**
   * Registers the user with given details
   */
  editProfileAPI = (email, password) => {
    return new Promise((resolve, reject) => {
      firebase
        .auth()
        .createUserWithEmailAndPassword(email, password)
        .then(
          (user) => {
            resolve(firebase.auth().currentUser);
          },
          (error) => {
            reject(this._handleError(error));
          }
        );
    });
  };

  /**
   * Login user with given details
   */
  loginUser = (email, password) => {
    return new Promise((resolve, reject) => {
      firebase
        .auth()
        .signInWithEmailAndPassword(email, password)
        .then(
          (user) => {
            resolve(firebase.auth().currentUser);
          },
          (error) => {
            reject(this._handleError(error));
          }
        );
    });
  };

  /**
   * forget Password user with given details
   */
  forgetPassword = (email) => {
    return new Promise((resolve, reject) => {
      firebase
        .auth()
        .sendPasswordResetEmail(email, {
          url:
            window.location.protocol + "//" + window.location.host + "/login",
        })
        .then(() => {
          resolve(true);
        })
        .catch((error) => {
          // Handle specific error codes from Firebase SDK
          let errorMessage = error.message;

          // Check for Firebase REST API error format
          if (error.error && error.error.message) {
            const firebaseErrorCode = error.error.message;
            if (firebaseErrorCode === 'EMAIL_NOT_FOUND') {
              errorMessage = 'No account found with this email address.';
            } else if (firebaseErrorCode === 'INVALID_EMAIL') {
              errorMessage = 'Invalid email address format.';
            } else {
              errorMessage = firebaseErrorCode;
            }
          }
          // Check for Firebase SDK error codes
          else if (error.code === 'auth/user-not-found') {
            errorMessage = 'No account found with this email address.';
          } else if (error.code === 'auth/invalid-email') {
            errorMessage = 'Invalid email address format.';
          } else if (error.code === 'auth/too-many-requests') {
            errorMessage = 'Too many requests. Please try again later.';
          }

          reject(new Error(errorMessage));
        });
    });
  };

  /**
   * Logout the user
   */
  logout = () => {
    return new Promise((resolve, reject) => {
      firebase
        .auth()
        .signOut()
        .then(() => {
          resolve(true);
        })
        .catch((error) => {
          reject(this._handleError(error));
        });
    });
  };

  /**
   * Social Login user with given details
   */
  socialLoginUser = async (type) => {
    let provider;
    if (type === "google") {
      provider = new firebase.auth.GoogleAuthProvider();
    } else if (type === "facebook") {
      provider = new firebase.auth.FacebookAuthProvider();
    }
    try {
      const result = await firebase.auth().signInWithPopup(provider);
      const user = result.user;
      return user;
    } catch (error) {
      throw this._handleError(error);
    }
  };

  addNewUserToFirestore = (user) => {
    const collection = firebase.firestore().collection("users");
    const { profile } = user.additionalUserInfo;
    const details = {
      firstName: profile.given_name ? profile.given_name : profile.first_name,
      lastName: profile.family_name ? profile.family_name : profile.last_name,
      fullName: profile.name,
      email: profile.email,
      picture: profile.picture,
      createdDtm: firebase.firestore.FieldValue.serverTimestamp(),
      lastLoginTime: firebase.firestore.FieldValue.serverTimestamp(),
    };
    collection.doc(firebase.auth().currentUser.uid).set(details);
    return { user, details };
  };

  setLoggeedInUser = (user) => {
    localStorage.setItem("authUser", JSON.stringify(user));
  };

  /**
   * Returns the authenticated user
   */
  getAuthenticatedUser = () => {
    if (!localStorage.getItem("authUser")) return null;
    return JSON.parse(localStorage.getItem("authUser"));
  };

  /**
   * Handle the error
   * @param {*} error
   */
  _handleError(error) {
    console.error(error);
    // var errorCode = error.code;
    var errorMessage = error.message;
    return errorMessage;
  }
}

let _fireBaseBackend = null;

/**
 * Initilize the backend
 * @param {*} config
 */
const initFirebaseBackend = (config) => {
  if (!_fireBaseBackend) {
    _fireBaseBackend = new FirebaseAuthBackend(config);
  }
  return _fireBaseBackend;
};

/**
 * Returns the firebase backend
 */
const getFirebaseBackend = () => {
  return _fireBaseBackend;
};

const getDb = () => {
  return _fireBaseBackend.firestore;
};

const getFirebaseAuthToken = async () => {
  try {
    const firebaseBackend = getFirebaseBackend();
    if (firebaseBackend.auth.currentUser) {
      const tokenResponse = await firebaseBackend.auth.currentUser.getIdToken();
      // console.log("tokenResponse", tokenResponse);
      return tokenResponse;
    }
  } catch (err) {
    console.log("Error getting token", err);
  }
};

const editProfileDetails = async (userId, userDetails) => {
  try {
    const firebaseBackend = getFirebaseBackend();
    const response = await firebaseBackend.firestore
      .collection("dashboard_users")
      .doc(userId)
      .update(userDetails);
    return response;
  } catch (error) {
    console.log(error);
  }
};

const verifyPassword = async (currentPassword) => {
  const firebaseBackend = getFirebaseBackend();
  const firebaseUser = firebaseBackend.auth.currentUser;
  if (!firebaseUser) {
    throw new Error("No user logged in");
  }

  try {
    const cred = firebase.auth.EmailAuthProvider.credential(
      firebaseUser.email,
      currentPassword
    );
    await firebaseUser.reauthenticateWithCredential(cred);
    return true;
  } catch (error) {
    console.error("Error verifying password", error);
    throw error;
  }
};

const changeUserEmail = async (newEmail, currentPassword) => {
  const firebaseBackend = getFirebaseBackend();
  const firebaseUser = firebaseBackend.auth.currentUser;

  if (!firebaseUser) {
    throw new Error("No user logged in");
  }

  try {
    // Verify current password
    const isPasswordCorrect = await verifyPassword(currentPassword);
    if (!isPasswordCorrect) {
      throw new Error("Current password is incorrect.");
    }

    // Update email in Firebase Authentication
    await verifyBeforeUpdateEmail(firebaseUser, newEmail);

    return true;
  } catch (error) {
    console.error("Error updating email:", error);

    if (error.code === "auth/invalid-login-credentials") {
      throw new Error("Current password is incorrect.");
    }

    if (error.code === "auth/requires-recent-login") {
      throw new Error("For security reasons, please log out and log back in before changing your email.");
    }

    if (error.code === "auth/email-already-in-use") {
      throw new Error("An account with this email already exists.");
    }

    throw error;
  }
};

// const changeUserEmail = async (newEmail, currentPassword) => {
//   const firebaseBackend = getFirebaseBackend();
//   const firebaseUser = firebaseBackend.auth.currentUser;

//   if (!firebaseUser) {
//     throw new Error("No user logged in");
//   }

//   try {
//     // Verify current password
//     const isPasswordCorrect = await verifyPassword(currentPassword);
//     if (!isPasswordCorrect) {
//       throw new Error("Current password is incorrect.");
//     }

//     // Check if the new email already exists
//     const existingUser = await firebase.auth().fetchSignInMethodsForEmail(
//       newEmail
//     );

//     console.log("existing user:", existingUser);
//     console.log

//     if (existingUser) {
//       throw new Error("An account with this email already exists.");
//     }

//     // Update email and Firestore document
//     await verifyBeforeUpdateEmail(firebaseUser, newEmail);
//     const userDocRef = firebaseBackend.firestore
//       .collection("dashboard_users")
//       .doc(firebaseUser.uid);
//     await userDocRef.update({ email: newEmail });

//     return true;
//   } catch (error) {
//     console.error("Error updating email", error);

//     // Catch case error is from firebase i.e auth/invalid-login-credentials
//     if (error.code === "auth/invalid-login-credentials") {
//       throw new Error("Current password is incorrect.");
//     }

//     throw error;
//   }
// };

const changeUserPassword = async (newPassword, currentPassword) => {
  const firebaseBackend = getFirebaseBackend();
  const firebaseUser = firebaseBackend.auth.currentUser;
  if (!firebaseUser) {
    throw new Error("No user logged in");
  }

  try {
    const isPasswordCorrect = await verifyPassword(currentPassword);
    if (!isPasswordCorrect) {
      throw new Error("Current password is incorrect");
    }
    await firebaseUser.updatePassword(newPassword);
    return true;
  } catch (error) {
    console.error("Error updating password", error);
    throw error;
  }
};

export {
  initFirebaseBackend,
  getFirebaseBackend,
  getFirebaseAuthToken,
  getDb,
  editProfileDetails,
  changeUserEmail,
  changeUserPassword,
};
